import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchCompanyInventory } from '../../api/companyInventory';
import {
  approveFinalInspection,
  deleteFinalInspection,
  deleteFinalInspectionAttachment,
  downloadFinalInspectionAttachment,
  downloadFinalInspectionPdf,
  fetchFinalInspection,
  fetchFinalInspections,
  revokeFinalInspection,
  uploadFinalInspectionAttachment,
} from '../../api/finalInspection';
import { useCompanyStore } from '../../store/companyStore';
import { APPROVAL_TARGET_FINAL_INSPECTION } from '../../types/approval';
import { CompanyInventoryItem, formatInventoryQty } from '../../types/companyInventory';
import { canConfirmInspection } from '../../types/companyRole';
import { FinalInspection, finalInspectionStatusLabel } from '../../types/finalInspection';
import {
  buildNcrDefectDesc,
  hasNonconformance,
} from '../../types/nonconformanceReport';
import { PRODUCT_SUB_NCR } from '../layout/Header';
import ApprovalCancelButton from '../approval/ApprovalCancelButton';
import ApprovalRequestModal from '../approval/ApprovalRequestModal';
import FinalInspectionCreate from './FinalInspectionCreate';
import FinalInspectionSheet from './FinalInspectionSheet';
import '../contract/ContractPanel.css';
import '../equipment/EquipTabPanel.css';
import './ProductForm.css';
import './InspectionListShared.css';
import {notifyError, reportError} from '../../utils/notify';

function downloadBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}

type View =
  | { type: 'list' }
  | { type: 'create' }
  | { type: 'edit'; inspectionId: number }
  | { type: 'detail'; inspectionId: number };

const LIST_VIEW: View = { type: 'list' };

function FinalInspectionPanel({
  contractId = null,
  embedded = false,
}: {
  contractId?: number | null;
  embedded?: boolean;
} = {}) {
  void embedded;
  const navigate = useNavigate();
  const companyId = useCompanyStore((state) => state.companyId);
  const role = useCompanyStore((state) => state.role);
  const [view, setView] = useState<View>(LIST_VIEW);
  const [inventory, setInventory] = useState<CompanyInventoryItem[]>([]);
  const [rows, setRows] = useState<FinalInspection[]>([]);
  const [detail, setDetail] = useState<FinalInspection | null>(null);
  const [filterId, setFilterId] = useState<number | null>(null);
  const [keyword, setKeyword] = useState('');
  const [approvalOpen, setApprovalOpen] = useState(false);
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async (inventoryId?: number | null, q?: string) => {
    try {
      const [stock, inspections] = await Promise.all([
        fetchCompanyInventory({ keyword: q }),
        fetchFinalInspections({
          inventoryId: inventoryId ?? undefined,
          contractId: contractId ?? undefined,
          keyword: q,
        }),
      ]);
      setInventory(stock);
      setRows(inspections);
      setError('');
    } catch {
      reportError(setError, '제품검사 목록을 불러오지 못했습니다.');
    }
  }, [contractId]);

  useEffect(() => {
    setView(LIST_VIEW);
    setFilterId(null);
    setKeyword('');
    void load();
  }, [companyId, load, setView]);

  useEffect(() => {
    if (view.type !== 'list') return;
    const timer = window.setTimeout(() => void load(filterId, keyword), 250);
    return () => window.clearTimeout(timer);
  }, [filterId, keyword, load, view.type]);

  useEffect(() => {
    if (view.type !== 'detail') {
      setDetail(null);
      return;
    }
    void fetchFinalInspection(view.inspectionId)
      .then(setDetail)
      .catch(() => reportError(setError, '제품검사 상세를 불러오지 못했습니다.'));
  }, [view]);

  const goList = () => {
    setView(LIST_VIEW);
    void load(filterId, keyword);
  };

  const handleDownloadPdf = async () => {
    if (!detail) return;
    try {
      setPdfDownloading(true);
      setError('');
      const name = (detail.itemName || '제품검사').replace(/[\\/:*?"<>|]+/g, '_');
      const lot = detail.lotNo?.trim() ? `_Lot${detail.lotNo.trim()}` : '';
      await downloadFinalInspectionPdf(detail.inspectionId, `제품검사성적서_${name}${lot}.pdf`);
    } catch (err) {
      const message =
        err instanceof Error && err.message
          ? err.message
          : (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
            'PDF 다운로드에 실패했습니다. Gotenberg 실행 여부를 확인해 주세요.';
      notifyError(message);
    } finally {
      setPdfDownloading(false);
    }
  };

  const detailAttachmentsEditable =
    detail?.status === 'DRAFT' && !detail.approvalInProgress;

  const handleDetailAttachmentUpload = async (files: File[]) => {
    if (!detail || !detailAttachmentsEditable || files.length === 0) return;
    try {
      for (const file of files) {
        await uploadFinalInspectionAttachment(detail.inspectionId, file);
      }
      setDetail(await fetchFinalInspection(detail.inspectionId));
      void load(filterId, keyword);
    } catch {
      reportError(setError, '첨부파일 업로드에 실패했습니다.');
    }
  };

  const handleDetailAttachmentDelete = async (file: { attachmentId: number; fileName: string }) => {
    if (!detail || !detailAttachmentsEditable) return;
    if (!window.confirm(`"${file.fileName}" 첨부를 삭제할까요?`)) return;
    try {
      await deleteFinalInspectionAttachment(detail.inspectionId, file.attachmentId);
      setDetail(await fetchFinalInspection(detail.inspectionId));
      void load(filterId, keyword);
    } catch {
      reportError(setError, '첨부파일 삭제에 실패했습니다.');
    }
  };

  return (
    <main className="contract-panel">
      <aside className="contract-panel__sidebar">
        <div className="contract-panel__side-header">
          <strong>제품검사</strong>
          <span>회사 재고</span>
        </div>
        <div className="contract-panel__search">
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="품명 / 규격 / LOT 검색"
          />
        </div>
        <div className="contract-panel__list-wrap">
          <ul className="contract-panel__list insp-side-list">
            <li>
              <button
                type="button"
                className={`contract-panel__list-item${filterId == null ? ' is-active' : ''}`}
                onClick={() => {
                  setFilterId(null);
                  setView(LIST_VIEW);
                }}
              >
                전체 보기
              </button>
            </li>
            {inventory.map((item) => (
              <li key={item.inventoryId}>
                <button
                  type="button"
                  className={`contract-panel__list-item${
                    filterId === item.inventoryId ? ' is-active' : ''
                  }`}
                  onClick={() => {
                    setFilterId(item.inventoryId);
                    setView(LIST_VIEW);
                  }}
                >
                  <span className="contract-panel__list-name">{item.itemName}</span>
                  <span className="contract-panel__list-meta">
                    {item.specText || '규격 없음'} · 재고 {formatInventoryQty(item.stockQty)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </aside>
      <section className="contract-panel__main" style={{ overflow: 'auto' }}>
        {view.type === 'create' || view.type === 'edit' ? (
          <FinalInspectionCreate
            inspectionId={view.type === 'edit' ? view.inspectionId : undefined}
            contractId={contractId}
            onCancel={
              view.type === 'edit'
                ? () => setView({ type: 'detail', inspectionId: view.inspectionId })
                : goList
            }
            onSaved={(inspectionId) => setView({ type: 'detail', inspectionId })}
          />
        ) : view.type === 'detail' ? (
          <div className="equip-tab-panel" style={{ padding: 16 }}>
            <div className="equip-tab-panel__toolbar">
              <div>
                <h3>제품검사 상세</h3>
                <p>
                  {detail
                    ? `${detail.itemName || '-'} · ${finalInspectionStatusLabel(detail.status, detail.approvalInProgress)}`
                    : '불러오는 중...'}
                </p>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {detail?.status !== 'APPROVED' && !detail?.approvalInProgress ? (
                  <button
                    type="button"
                    onClick={() =>
                      detail && setView({ type: 'edit', inspectionId: detail.inspectionId })
                    }
                  >
                    수정
                  </button>
                ) : null}
                {detail?.status === 'APPROVED' ? (
                  <button
                    type="button"
                    disabled={pdfDownloading}
                    onClick={() => void handleDownloadPdf()}
                  >
                    {pdfDownloading ? 'PDF 생성 중...' : 'PDF 다운로드'}
                  </button>
                ) : null}
                {detail?.canRevoke ? (
                  <button
                    type="button"
                    className="is-danger"
                    onClick={() => {
                      if (
                        !detail ||
                        !window.confirm(
                          '승인을 철회할까요?\n승인란·검토란이 지워지고, 반영된 재고 입고도 되돌립니다.'
                        )
                      ) {
                        return;
                      }
                      void revokeFinalInspection(detail.inspectionId)
                        .then(setDetail)
                        .catch((err) => {
                          notifyError(
                            (err as { response?: { data?: { message?: string } } })?.response?.data
                              ?.message ?? '승인 철회에 실패했습니다.'
                          );
                        });
                    }}
                  >
                    승인 철회
                  </button>
                ) : null}
                {detail?.canCancelApproval ? (
                  <ApprovalCancelButton
                    detail={detail}
                    className="is-ghost"
                    onCancelled={async () => {
                      if (!detail) return;
                      setDetail(await fetchFinalInspection(detail.inspectionId));
                      void load(filterId, keyword);
                    }}
                  />
                ) : null}
                {detail?.status !== 'APPROVED' && !detail?.approvalInProgress ? (
                  <button type="button" onClick={() => setApprovalOpen(true)}>
                    결재 요청
                  </button>
                ) : null}
                {detail?.status !== 'APPROVED' && !detail?.approvalInProgress && canConfirmInspection(role) ? (
                  <button
                    type="button"
                    onClick={() => {
                      if (
                        detail &&
                        window.confirm('제품검사를 바로 승인할까요? LOT 수량이 재고에 입고됩니다.')
                      ) {
                        void approveFinalInspection(detail.inspectionId)
                          .then(setDetail)
                          .catch(() => reportError(setError, '승인에 실패했습니다.'));
                      }
                    }}
                  >
                    승인
                  </button>
                ) : null}
                {detail && hasNonconformance(detail.overallJudgment, detail.items) ? (
                  <button
                    type="button"
                    onClick={() =>
                      navigate('/app', {
                        state: {
                          activeMenu: PRODUCT_SUB_NCR,
                          ncrPrefill: {
                            sourceType: 'FINAL',
                            sourceInspectionId: detail.inspectionId,
                            inventoryId: detail.inventoryId ?? null,
                            itemName: detail.itemName || '',
                            specText: detail.specText || '',
                            lotNo: detail.lotNo || '',
                            qty: detail.lotQty || '',
                            reporterName: detail.inspectorName || detail.authorName || '',
                            reportDate: detail.inspectionDate?.slice(0, 10) || undefined,
                            defectDesc: buildNcrDefectDesc({
                              sourceLabel: '제품검사',
                              inspectionId: detail.inspectionId,
                              overallJudgment: detail.overallJudgment,
                              items: detail.items,
                              remark: detail.remark,
                            }),
                          },
                        },
                      })
                    }
                  >
                    부적합품 보고서
                  </button>
                ) : null}
                {detail?.status !== 'APPROVED' && !detail?.approvalInProgress ? (
                  <button
                    type="button"
                    className="is-danger"
                    onClick={() => {
                      if (detail && window.confirm('이 제품검사를 삭제할까요?')) {
                        void deleteFinalInspection(detail.inspectionId).then(goList);
                      }
                    }}
                  >
                    삭제
                  </button>
                ) : null}
                <button type="button" className="is-ghost" onClick={goList}>
                  목록
                </button>
              </div>
            </div>
            {error ? <p className="equip-tab-panel__error">{error}</p> : null}
            {detail?.approvalInProgress ? (
              <p className="equip-tab-panel__hint">결재가 진행 중입니다. 내용을 바꾸려면 결재 요청을 취소한 뒤 수정하고 다시 요청하세요.</p>
            ) : null}
            {detail ? (
              <>
                <FinalInspectionSheet
                  values={{
                    inventoryId: detail.inventoryId,
                    itemKind: detail.itemKind ?? '',
                    itemName: detail.itemName ?? '',
                    specText: detail.specText ?? '',
                    authorName: detail.authorName ?? '',
                    reviewerName: detail.reviewerName ?? '',
                    approverName: detail.approverName ?? '',
                    lotNo: detail.lotNo ?? '',
                    lotQty: detail.lotQty ?? '',
                    inspectorName: detail.inspectorName ?? '',
                    inspectionDate: detail.inspectionDate?.slice(0, 10) ?? '',
                    overallJudgment: detail.overallJudgment ?? '',
                    remark: detail.remark ?? '',
                    items: detail.items ?? [],
                  }}
                  remarkAttachments={detail.attachments ?? []}
                  attachmentsEditable={detailAttachmentsEditable}
                  onPickRemarkFiles={(files) => void handleDetailAttachmentUpload(files)}
                  onDeleteAttachment={(file) => void handleDetailAttachmentDelete(file)}
                  onDownloadAttachment={(file) => {
                    void downloadFinalInspectionAttachment(detail.inspectionId, file.attachmentId)
                      .then((blob) => downloadBlob(blob, file.fileName))
                      .catch(() => reportError(setError, '첨부파일 다운로드에 실패했습니다.'));
                  }}
                />
                <div
                  className="equip-tab-panel__toolbar"
                  style={{ marginTop: 16, justifyContent: 'flex-end' }}
                >
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    {detail?.status !== 'APPROVED' && !detail?.approvalInProgress ? (
                      <button
                        type="button"
                        onClick={() =>
                          detail && setView({ type: 'edit', inspectionId: detail.inspectionId })
                        }
                      >
                        수정
                      </button>
                    ) : null}
                    {detail?.status === 'APPROVED' ? (
                      <button
                        type="button"
                        disabled={pdfDownloading}
                        onClick={() => void handleDownloadPdf()}
                      >
                        {pdfDownloading ? 'PDF 생성 중...' : 'PDF 다운로드'}
                      </button>
                    ) : null}
                    {detail?.canRevoke ? (
                      <button
                        type="button"
                        className="is-danger"
                        onClick={() => {
                          if (
                            !detail ||
                            !window.confirm(
                              '승인을 철회할까요?\n승인란·검토란이 지워지고, 반영된 재고 입고도 되돌립니다.'
                            )
                          ) {
                            return;
                          }
                          void revokeFinalInspection(detail.inspectionId)
                            .then(setDetail)
                            .catch((err) => {
                              notifyError(
                                (err as { response?: { data?: { message?: string } } })?.response?.data
                                  ?.message ?? '승인 철회에 실패했습니다.'
                              );
                            });
                        }}
                      >
                        승인 철회
                      </button>
                    ) : null}
                    {detail?.canCancelApproval ? (
                      <ApprovalCancelButton
                        detail={detail}
                        className="is-ghost"
                        onCancelled={async () => {
                          if (!detail) return;
                          setDetail(await fetchFinalInspection(detail.inspectionId));
                          void load(filterId, keyword);
                        }}
                      />
                    ) : null}
                    {detail?.status !== 'APPROVED' && !detail?.approvalInProgress ? (
                      <button type="button" onClick={() => setApprovalOpen(true)}>
                        결재 요청
                      </button>
                    ) : null}
                    {detail?.status !== 'APPROVED' && !detail?.approvalInProgress && canConfirmInspection(role) ? (
                      <button
                        type="button"
                        onClick={() => {
                          if (
                            detail &&
                            window.confirm('제품검사를 바로 승인할까요? LOT 수량이 재고에 입고됩니다.')
                          ) {
                            void approveFinalInspection(detail.inspectionId)
                              .then(setDetail)
                              .catch(() => reportError(setError, '승인에 실패했습니다.'));
                          }
                        }}
                      >
                        승인
                      </button>
                    ) : null}
                    {detail && hasNonconformance(detail.overallJudgment, detail.items) ? (
                      <button
                        type="button"
                        onClick={() =>
                          navigate('/app', {
                            state: {
                              activeMenu: PRODUCT_SUB_NCR,
                              ncrPrefill: {
                                sourceType: 'FINAL',
                                sourceInspectionId: detail.inspectionId,
                                inventoryId: detail.inventoryId ?? null,
                                itemName: detail.itemName || '',
                                specText: detail.specText || '',
                                lotNo: detail.lotNo || '',
                                qty: detail.lotQty || '',
                                reporterName: detail.inspectorName || detail.authorName || '',
                                reportDate: detail.inspectionDate?.slice(0, 10) || undefined,
                                defectDesc: buildNcrDefectDesc({
                                  sourceLabel: '제품검사',
                                  inspectionId: detail.inspectionId,
                                  overallJudgment: detail.overallJudgment,
                                  items: detail.items,
                                  remark: detail.remark,
                                }),
                              },
                            },
                          })
                        }
                      >
                        부적합품 보고서
                      </button>
                    ) : null}
                    {detail?.status !== 'APPROVED' && !detail?.approvalInProgress ? (
                      <button
                        type="button"
                        className="is-danger"
                        onClick={() => {
                          if (detail && window.confirm('이 제품검사를 삭제할까요?')) {
                            void deleteFinalInspection(detail.inspectionId).then(goList);
                          }
                        }}
                      >
                        삭제
                      </button>
                    ) : null}
                    <button type="button" className="is-ghost" onClick={goList}>
                      목록
                    </button>
                  </div>
                </div>
                <ApprovalRequestModal
                  open={approvalOpen}
                  titleDefault={`${detail.itemName || '-'} 제품검사성적서 결재`}
                  targetType={APPROVAL_TARGET_FINAL_INSPECTION}
                  targetId={detail.inspectionId}
                  onClose={() => setApprovalOpen(false)}
                  onSubmitted={() => { setApprovalOpen(false); if (detail) void fetchFinalInspection(detail.inspectionId).then(setDetail); void load(filterId, keyword); }}
                />
              </>
            ) : null}
          </div>
        ) : (
          <>
            <div className="contract-panel__toolbar">
              <div>
                <h2>제품검사 현황</h2>
                <p>표시 {rows.length}건 · 승인 시 LOT 수량이 재고에 입고됩니다</p>
              </div>
              <button
                type="button"
                className="contract-panel__primary"
                onClick={() => setView({ type: 'create' })}
              >
                제품검사 등록
              </button>
            </div>
            {error ? <p className="equip-tab-panel__error">{error}</p> : null}
            <div className="contract-panel__table-wrap">
              <table className="ec-table insp-list-table">
                <thead>
                  <tr>
                    <th style={{ width: 48 }}>No</th>
                    <th>모델</th>
                    <th style={{ width: 100 }}>규격</th>
                    <th style={{ width: 110 }}>양식</th>
                    <th style={{ width: 168 }}>LOT</th>
                    <th style={{ width: 72 }}>수량</th>
                    <th style={{ width: 88 }}>검사자</th>
                    <th style={{ width: 100 }}>검사일</th>
                    <th style={{ width: 88 }}>상태</th>
                    <th style={{ width: 72 }}>판정</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => (
                    <tr
                      key={row.inspectionId}
                      onClick={() => setView({ type: 'detail', inspectionId: row.inspectionId })}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>{rows.length - index}</td>
                      <td title={row.itemName || undefined}>{row.itemName || '-'}</td>
                      <td title={row.specText || undefined}>{row.specText || '-'}</td>
                      <td title={row.formTitle || undefined}>{row.formTitle || '-'}</td>
                      <td title={row.lotNo || undefined}>{row.lotNo || '-'}</td>
                      <td>{row.lotQty || '-'}</td>
                      <td title={row.inspectorName || undefined}>{row.inspectorName || '-'}</td>
                      <td>{row.inspectionDate?.slice(0, 10) || '-'}</td>
                      <td>{finalInspectionStatusLabel(row.status, row.approvalInProgress)}</td>
                      <td>{row.overallJudgment || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </section>
    </main>
  );
}

export default FinalInspectionPanel;
